import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ChevronDown, ChevronUp, Trash2, AlertTriangle, ArrowUp, ArrowDown } from "lucide-react";
import { STAP_TYPES, nieuweStap, alleStappen, stapSamenvatting } from "@/lib/campagneStappen";
import { StapConfig } from "@/components/campagne/StapConfig";

function BouwsteenKiezer({ onKies }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative flex justify-center py-1">
      <button type="button" onClick={() => setOpen(o => !o)} data-testid="stap-toevoegen-btn"
        className="h-7 w-7 rounded-full border border-dashed border-primary/50 text-primary flex items-center justify-center hover:bg-primary hover:text-white transition-colors">
        <Plus className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute top-9 z-20 bg-card border border-border rounded-xl shadow-lg p-2 grid grid-cols-3 gap-1 w-[420px]" data-testid="bouwsteen-kiezer">
          {Object.entries(STAP_TYPES).map(([k, d]) => { const I = d.icon; return (
            <button key={k} type="button" onClick={() => { onKies(k); setOpen(false); }} data-testid={`bouwsteen-${k}`}
              className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-xs text-left hover:scale-[1.02] transition-transform ${d.kleur}`}>
              <I className="h-3.5 w-3.5 shrink-0" />{d.label}
            </button>); })}
        </div>
      )}
    </div>
  );
}

function StapKaart({ stap, onChange, onRemove, onMove, ctx, diepte, tellers }) {
  const [open, setOpen] = useState(false);
  const def = STAP_TYPES[stap.type];
  const I = def.icon;
  const fouten = (ctx.validatie || []).filter(f => f.includes(`'${stap.naam}'`) || f.includes(stap.id.slice(0, 6)));
  const takken = stap.takken || {};
  const takKeys = Object.keys(takken);
  const voegTak = (code) => { if (!code || takken[code]) return; onChange({ ...stap, takken: { ...takken, [code]: [] } }); };
  const t = tellers?.[stap.id];
  return (
    <div className="space-y-1" data-testid={`stap-kaart-${stap.type}`}>
      <div className={`rounded-xl border bg-card transition-shadow ${open ? "shadow-md" : "hover:shadow-sm"} ${fouten.length ? "border-destructive/60" : "border-border"}`}>
        <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer" onClick={() => setOpen(o => !o)} data-testid="stap-kaart-kop">
          <span className={`h-8 w-8 rounded-lg border flex items-center justify-center shrink-0 ${def.kleur}`}><I className="h-4 w-4" /></span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{def.label}</span>
              {fouten.length > 0 && <Badge variant="destructive" className="h-4 px-1.5 text-[10px]" data-testid="stap-validatie-badge"><AlertTriangle className="h-3 w-3 mr-0.5" />{fouten.length}</Badge>}
              {t && <span className="text-[10px] text-muted-foreground" data-testid="stap-tellers">nu hier {t.nu_hier} · gedaan {t.gedaan}</span>}
            </div>
            <div className="text-sm font-medium truncate">{stap.naam}</div>
            <div className="text-xs text-muted-foreground truncate">{stapSamenvatting(stap)}</div>
          </div>
          <div className="flex items-center gap-0.5" onClick={e => e.stopPropagation()}>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onMove(-1)} data-testid="stap-omhoog"><ArrowUp className="h-3.5 w-3.5" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onMove(1)} data-testid="stap-omlaag"><ArrowDown className="h-3.5 w-3.5" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={onRemove} data-testid="stap-verwijder"><Trash2 className="h-3.5 w-3.5" /></Button>
            {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </div>
        </div>
        {open && (
          <div className="border-t border-border px-4 py-4 space-y-4 bg-muted/20" data-testid="stap-config">
            <div className="space-y-1"><Label className="text-xs text-muted-foreground">Naam van de stap</Label>
              <Input value={stap.naam} onChange={e => onChange({ ...stap, naam: e.target.value })} data-testid="stap-naam" /></div>
            <StapConfig stap={stap} onChange={onChange} {...ctx} stappen={alleStappen(ctx.rootStappen)} />
            {fouten.length > 0 && <ul className="text-xs text-destructive list-disc pl-4">{fouten.map((f, i) => <li key={i}>{f}</li>)}</ul>}
            {stap.type === "beltaak" && (
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground">Extra tak per uitkomst:</Label>
                <Select value="" onValueChange={voegTak}>
                  <SelectTrigger className="w-56 h-8 text-xs" data-testid="beltaak-extra-tak"><SelectValue placeholder="kies uitkomst…" /></SelectTrigger>
                  <SelectContent>{(ctx.uitkomsten || []).filter(u => !takken[u.code]).map(u => <SelectItem key={u.code} value={u.code}>{u.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}
      </div>
      {takKeys.length > 0 && (
        <div className={`grid gap-3 pl-4 ${takKeys.length === 1 ? "grid-cols-1" : takKeys.length === 2 ? "grid-cols-2" : "grid-cols-3"}`} data-testid="stap-takken">
          {takKeys.map(k => (
            <div key={k} className="rounded-xl border border-dashed border-border/80 p-2 bg-muted/10">
              <div className="flex items-center justify-between mb-1">
                <span className={`text-[11px] font-semibold uppercase tracking-wide ${k === "ja" ? "text-emerald-700" : k === "nee" ? "text-rose-700" : "text-violet-700"}`}>{k === "ja" ? "Ja" : k === "nee" ? "Nee" : (ctx.uitkomsten || []).find(u => u.code === k)?.label || k}</span>
                {!["ja", "nee"].includes(k) && <button type="button" className="text-[10px] text-muted-foreground hover:text-destructive" onClick={() => { const n = { ...takken }; delete n[k]; onChange({ ...stap, takken: n }); }} data-testid="tak-verwijder">verwijder tak</button>}
              </div>
              {React.createElement(StapLijst, { stappen: takken[k], onChange: l => onChange({ ...stap, takken: { ...takken, [k]: l } }), ctx, diepte: diepte + 1, tellers })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function StapLijst({ stappen, onChange, ctx, diepte = 0, tellers }) {
  const lijst = stappen || [];
  const voeg = (i, type) => { const n = [...lijst]; n.splice(i, 0, nieuweStap(type)); onChange(n); };
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= lijst.length) return; const n = [...lijst]; [n[i], n[j]] = [n[j], n[i]]; onChange(n); };
  return (
    <div>
      <BouwsteenKiezer onKies={t => voeg(0, t)} />
      {lijst.map((s, i) => (
        <React.Fragment key={s.id}>
          <StapKaart stap={s} ctx={ctx} diepte={diepte} tellers={tellers}
            onChange={n => onChange(lijst.map(x => x.id === s.id ? n : x))}
            onRemove={() => onChange(lijst.filter(x => x.id !== s.id))}
            onMove={d => move(i, d)} />
          <BouwsteenKiezer onKies={t => voeg(i + 1, t)} />
        </React.Fragment>
      ))}
      {lijst.length === 0 && <p className="text-center text-xs text-muted-foreground italic py-1">{diepte ? "leeg → gaat door na de vertakking" : "Voeg een eerste bouwsteen toe"}</p>}
    </div>
  );
}

